import {VueController} from '@/service/support/vuex-controller';
import orgService from "@/service/org-service";

const TAXI_SYSTEM_KEY = 'taxi';

export default VueController("TaxiSettings", {
	state: [
		"organization",
		"cognito"
	],
	data() {
		return {
			apiKey: '',
			signingSecret: '',
			showSecret: false,
			saving: false,
			disabling: false,
			showCredentialForm: false
		};
	},
	computed: {
		canManageIntegration() {
			if (!this.organization) return false;
			const userType = this.cognito && this.cognito.attributes ? this.cognito.attributes["custom:mutableUserType"] : null;
			return userType === 'admin';
		},
		isEnterpriseTier() {
			const tier = this.organization ? this.organization.tier : '';
			return tier === 'enterprise';
		},
		// Vuex "organizationId" can be the placeholder "none"; the loaded organization record is authoritative.
		orgId() {
			return this.organization && this.organization.id ? this.organization.id : null;
		},
		taxiIntegration() {
			return this.organization && this.organization.taxiIntegration ? this.organization.taxiIntegration : null;
		},
		isEnabled() {
			return Boolean(this.taxiIntegration && this.taxiIntegration.enabled === true);
		},
		keyHint() {
			return this.taxiIntegration && this.taxiIntegration.key_hint ? this.taxiIntegration.key_hint : '';
		},
		updatedDisplay() {
			if (!this.taxiIntegration || !this.taxiIntegration.updated_at) return '';
			return new Date(this.taxiIntegration.updated_at).toLocaleString();
		},
		lastError() {
			return this.taxiIntegration && this.taxiIntegration.last_error ? this.taxiIntegration.last_error : '';
		},
		lastErrorDisplay() {
			if (!this.taxiIntegration || !this.taxiIntegration.last_error_at) return '';
			return new Date(this.taxiIntegration.last_error_at).toLocaleString();
		},
		hasTaxiExternalSystem() {
			const systems = this.organization && this.organization.externalSystems ? this.organization.externalSystems : [];
			return systems.some(s => String(s).toLowerCase() === TAXI_SYSTEM_KEY);
		},
		isFormValid() {
			const key = this.apiKey ? this.apiKey.trim() : '';
			const secret = this.signingSecret ? this.signingSecret.trim() : '';
			return key.length >= 16 && secret.length >= 32 && !/\s/.test(key) && !/\s/.test(secret);
		}
	},
	methods: {
		openCredentialForm() {
			this.apiKey = '';
			this.signingSecret = '';
			this.showSecret = false;
			this.showCredentialForm = true;
		},

		cancelCredentialForm() {
			this.apiKey = '';
			this.signingSecret = '';
			this.showCredentialForm = false;
		},

		async saveCredential() {
			if (!this.isFormValid) {
				this.notification('error', 'Enter the API key and signing secret exactly as issued by Taxi');
				return;
			}
			this.saving = true;
			try {
				const state = await orgService.mutation("setTaxiCredential", {
					org_id: this.orgId,
					api_key: this.apiKey.trim(),
					signing_secret: this.signingSecret.trim()
				});
				this.$set(this.organization, 'taxiIntegration', state);
				await this.ensureTaxiExternalSystem();
				this.cancelCredentialForm();
				this.notification('success', 'Taxi credential verified and saved');
			} catch (error) {
				console.error("Error saving Taxi credential:", error);
				this.notification('error', 'Failed to save Taxi credential: ' + this.errorText(error));
			} finally {
				this.saving = false;
			}
		},

		async disableIntegration() {
			if (!confirm('Disable the Taxi integration? Pushes stop immediately and the stored credential is deleted. Portal links are kept and resume when you re-enter a credential.')) {
				return;
			}
			this.disabling = true;
			try {
				const state = await orgService.mutation("disableTaxiIntegration", {
					org_id: this.orgId
				});
				this.$set(this.organization, 'taxiIntegration', state);
				this.notification('success', 'Taxi integration disabled');
			} catch (error) {
				console.error("Error disabling Taxi integration:", error);
				this.notification('error', 'Failed to disable Taxi integration: ' + this.errorText(error));
			} finally {
				this.disabling = false;
			}
		},

		/**
		 * The per-portal "taxi" External System ID is what links a portal to a Taxi customer,
		 * so make sure the org has that external system defined once the integration is on.
		 */
		async ensureTaxiExternalSystem() {
			if (this.hasTaxiExternalSystem) return;
			const systems = (this.organization.externalSystems || []).slice();
			systems.push(TAXI_SYSTEM_KEY);
			try {
				await orgService.mutation("updateExternalSystems", { external_systems: systems });
				this.$set(this.organization, 'externalSystems', systems);
			} catch (error) {
				console.error("Unable to add taxi external system:", error);
				this.notification('warning', 'Credential saved, but the "taxi" External System could not be added. Add it under Preferences → External Systems.');
			}
		},

		errorText(error) {
			if (!error) return 'Unknown error';
			if (error.errors && error.errors.length > 0 && error.errors[0].message) return error.errors[0].message;
			return error.message || 'Unknown error';
		}
	}
});
